import dayjs from "dayjs";
import { useEffect } from "react";
import CryptoJS from "crypto-js";

export const initialsValue = (str = "") => {
  const strArr = str?.split(" ");
  const initials =
    (strArr &&
      Array.isArray(strArr) &&
      strArr[0]
        .slice(0, 1)
        .concat(strArr?.length > 1 ? strArr.at(-1).slice(0, 1) : "")
        .toUpperCase()) ||
    [];
  return initials;
};

export const formattedDate = (date) => {
  const parts = new Date(date)
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .split(" ");

  return `${parts[0]} ${parts[1]}, ${parts[2]}`;
};

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

export const handleOnDragEnd = (result, list = [], setList = () => {}, callback = () => {}) => {
  if (!result.destination) return;

  const items = reorder(list, result.source.index, result.destination.index);
  setList(items);
  callback(items);
};

export const createFilter = (filterArr = [], key) => {
  if (filterArr && typeof filterArr[0] === "boolean") {
    return { [key]: filterArr[0] };
  }
  return Array.isArray(filterArr) && filterArr.length > 0
    ? filterArr.reduce((acc, item, i) => {
        acc[`${key}[${i}]`] = item;
        return acc;
      }, {})
    : {};
};

export const handleFilterChange = (name, value, setFilters = () => {}) => {
  setFilters((prevFilters) => ({
    ...prevFilters,
    ...(name === "limit" && { page: 1 }),
    [name]: value,
  }));
};

export const isValidDate = (value, format) => {
  return dayjs(value, format, true).isValid();
};

export function useDisableZoom(shouldDisable) {
  useEffect(() => {
    const preventZoom = (e) => {
      // Prevent zoom only when the Ctrl key is pressed or pinch gestures are detected
      if (shouldDisable && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
      }
    };

    if (shouldDisable) {
      // Disable zoom for keyboard shortcuts and mouse events
      document.addEventListener("wheel", preventZoom, { passive: false });
      document.addEventListener("keydown", (e) => {
        if (e.ctrlKey && (e.key === "+" || e.key === "-" || e.key === "0")) {
          e.preventDefault();
        }
      });
    }

    return () => {
      if (shouldDisable) {
        // Cleanup event listeners
        document.removeEventListener("wheel", preventZoom);
        document.removeEventListener("keydown", preventZoom);
      }
    };
  }, [shouldDisable]);
}

// COLOR -LIGHTENER
export function lightenHexColor(hex, percent) {
  if (!hex || !/^#?[0-9A-Fa-f]{6}$/.test(hex)) {
    console.error("Invalid hex color:", hex);
    return null;
  }

  hex = hex.replace("#", "");

  const rgb = hex.match(/.{1,2}/g).map((x) => parseInt(x, 16));

  const r = rgb[0] / 255;
  const g = rgb[1] / 255;
  const b = rgb[2] / 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  l = Math.min(1, l + percent / 100);

  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r2, g2, b2;
  if (s === 0) {
    r2 = g2 = b2 = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r2 = hue2rgb(p, q, h + 1 / 3);
    g2 = hue2rgb(p, q, h);
    b2 = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (x) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r2)}${toHex(g2)}${toHex(b2)}`;
}

export function hexToRgb(hex) {
  if (!hex || !/^#?[0-9A-Fa-f]{6}$/.test(hex)) {
    console.error("Invalid hex color:", hex);
    return null;
  }
  hex = hex.replace(/^#/, "");

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;

  return { r, g, b, a };
}

export function hexToHsv(hex) {
  if (!hex || !/^#?[0-9A-Fa-f]{6}$/.test(hex)) {
    console.error("Invalid hex color:", hex);
    return null;
  }
  const { r, g, b, a = 1 } = hexToRgb(hex);

  const rScaled = r / 255;
  const gScaled = g / 255;
  const bScaled = b / 255;

  const max = Math.max(rScaled, gScaled, bScaled);
  const min = Math.min(rScaled, gScaled, bScaled);
  const delta = max - min;

  let h,
    s,
    v = max;

  if (delta === 0) {
    h = 0;
  } else if (max === rScaled) {
    h = ((gScaled - bScaled) / delta) % 6;
  } else if (max === gScaled) {
    h = (bScaled - rScaled) / delta + 2;
  } else {
    h = (rScaled - gScaled) / delta + 4;
  }

  h = Math.round(h * 60);
  if (h < 0) h += 360;

  s = max === 0 ? 0 : delta / max;
  s = +(s * 100).toFixed(1);
  v = +(v * 100).toFixed(1);

  return { h, s, v, a };
}

export function isValidColorCode(color) {
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  const rgbRegex = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/;

  if (hexRegex.test(color)) return "HEX";
  if (rgbRegex.test(color)) {
    const [, r, g, b] = color.match(rgbRegex);
    if ([r, g, b].every((n) => n >= 0 && n <= 255)) {
      return "RGB";
    }
  }
  return false;
}

export const updateList = (state, list, item, _id = null) => {
  switch (state) {
    case "add":
      return addItem(list, item, _id);
    case "edit":
      return editItem(list, item, _id);
    case "delete":
      return deleteItem(list, _id);
    default:
      return list;
  }
};

const addItem = (list, item, _id) => {
  if (!_id) {
    return [...list, item];
  }

  return list.map((entry) => {
    if (entry._id === _id) {
      entry.children = [...entry.children, item];
    } else if (entry.children && entry.children.length > 0) {
      entry.children = addItem(entry.children, item, _id);
    }
    return entry;
  });
};

const editItem = (list, item, _id) => {
  return list.map((entry) => {
    if (entry._id === _id) {
      return { ...entry, ...item };
    } else if (entry.children && entry.children.length > 0) {
      entry.children = editItem(entry.children, item, _id);
    }
    return entry;
  });
};

const deleteItem = (list, _id) => {
  return list.filter((entry) => {
    if (entry._id === _id) {
      return false;
    }
    if (entry.children && entry.children.length > 0) {
      entry.children = deleteItem(entry.children, _id);
    }
    return true;
  });
};

export const removeTag = (html) => {
  return html?.replace(/<[^>]*>/g, "");
};

export const replaceOldData = (oldData, newData, byKey = "_id") => {
  const dataMap = new Map(oldData.map((item) => [item[byKey], item]));

  newData.forEach((newItem) => {
    dataMap.set(newItem[byKey], newItem);
  });

  return Array.from(dataMap.values());
};

export const removeDuplicateFromArray = (arr = [], byKey = "_id") => {
  try {
    return arr?.filter((item, index, self) => index === self.findIndex((t) => t?.[byKey] === item?.[byKey]));
  } catch (error) {
    throw new Error("arguments shoulb be an array");
  }
};

export const encodeId = (dataObj) => {
  const stringifiedData = typeof dataObj === "string" ? dataObj : JSON.stringify(dataObj);
  return CryptoJS.AES.encrypt(stringifiedData, process.env.REACT_APP_SECRET_KEY).toString();
};

export const decodeId = (encryptedData) => {
  if (!encryptedData) return;

  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, process.env.REACT_APP_SECRET_KEY);
    const decryptedData = bytes.toString(CryptoJS.enc.Utf8);

    // Try parsing as JSON. If it fails, return as plain string.
    try {
      return JSON.parse(decryptedData);
    } catch {
      return decryptedData;
    }
  } catch (err) {
    console.error("Decryption failed", err);
    return null;
  }
};

export const getOrdinal = (num) => {
  if (typeof num !== "number" || num < 1) return num; // Handle invalid inputs

  let suffix = "th"; // Default suffix

  if (num % 100 < 11 || num % 100 > 13) {
    // Handle exceptions like 11th, 12th, 13th
    switch (num % 10) {
      case 1:
        suffix = "st";
        break;
      case 2:
        suffix = "nd";
        break;
      case 3:
        suffix = "rd";
        break;
    }
  }

  return num + suffix;
};

export const formatNumber = (num) => {
  if (!num) return;
  if (num >= 1_000_000_000_000) {
    return (num / 1_000_000_000_000).toFixed(1) + "T"; // Trillion
  } else if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1) + "B"; // Billion
  } else if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1) + "M"; // Million
  } else if (num >= 1_000) {
    return (num / 1_000).toFixed(1) + "K"; // Thousand
  }
  return num?.toString(); // Less than 1K
};

export const checkDateStatus = (date) => {
  if (!date) return;
  const inputDate = new Date(date);
  const today = new Date();

  // Remove time part for accurate comparison
  today.setHours(0, 0, 0, 0);
  inputDate.setHours(0, 0, 0, 0);

  if (inputDate < today) {
    return "past";
  } else if (inputDate > today) {
    return "upcoming";
  } else {
    return "current";
  }
};

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024; // base
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));

  return `${value} ${sizes[i]}`;
};

export const checkImageByExtention = (ext) => {
  const extensions = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "tif",
    "tiff",
    "webp",
    "heic",
    "heif",
    "psd",
    "raw",
    "cr2",
    "nef",
    "arw",
    "svg",
    "ai",
    "eps",
    "pdf",
    "ico",
    "dds",
    "tga",
    "exr",
  ];

  return extensions.includes(ext);
};

export const elementResize = (ref = {}, setMeasurement = () => {}) => {
  try {
    const handleResize = () => {
      const target = ref.current || document.body;
      const divWidth = target.clientWidth;
      const divHeight = target.offsetHeight;

      setMeasurement({
        width: divWidth,
        height: divHeight,
      });
    };

    // Initial call
    handleResize();

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(handleResize);
    });

    const target = ref.current || document.body;
    resizeObserver.observe(target);

    return () => {
      resizeObserver.disconnect();
    };
  } catch (error) {
    console.log("ResizeObserver error:", error);
  }
};

export const getElementPosition = (elm, setMesurement = () => {}) => {
  if (!elm) {
    return;
  }
  const updatePosition = () => {
    if (elm) {
      const rect = elm.getBoundingClientRect();
      setMesurement({
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        right: rect.right,
      });
    }
  };

  // Update position on load
  updatePosition();

  // Update position on scroll and resize
  window.addEventListener("scroll", updatePosition, true); // use capture to catch scrolls on ancestors
  window.addEventListener("resize", updatePosition);

  return () => {
    window.removeEventListener("scroll", updatePosition, true);
    window.removeEventListener("resize", updatePosition);
  };
};

export const clickOnElemnt = ({ event, property = "id" }) => {
  let target = event.target;

  while (target && !target.id) {
    target = target.closest(".editable-element:not(.not-changable)");
    if (target && target.id) break;
    target = target?.parentElement;
  }

  return target?.id || null;
};

export const spaceInCamel = (str) => {
  if (!str) {
    return;
  }
  return str.replace(/([a-z])([A-Z])/g, "$1 $2");
};

export const firstUppercase = (str) => {
  const words = str.toLowerCase().split(" ");
  if (words.length === 0) return "";

  // Capitalize first word, keep rest lowercase
  words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);

  return words.join(" ");
};

export const loadDataOnScroll = ({ event = null, metadata = null, setData = () => {} }) => {
  console.log({
    a: Math.ceil(event.target.scrollTop) + 20,
    b: event.target.scrollHeight - event.target.clientHeight,
    c: metadata?.hasNextPage,
  });
  if (
    Math.ceil(event.target.scrollTop) + 20 >= event.target.scrollHeight - event.target.clientHeight &&
    metadata?.hasNextPage
  ) {
    setData((pre) => ({
      ...pre,
      page: metadata?.nextPage,
    }));
  }
};

export const toSnakeCase = (str) => {
  return str
    .trim() // Remove leading/trailing whitespace
    .toLowerCase() // Convert entire string to lowercase
    .replace(/[^a-z0-9]+/g, "_") // Replace non-alphanumeric characters with underscores
    .replace(/^_+|_+$/g, ""); // Remove leading/trailing underscores
};

export const rgba = (color) => {
  return `rgba(${color?.r}, ${color?.g}, ${color?.b}, ${color?.a})`;
};

export function downloadFile(fileUrl, fileName) {
  const a = document.createElement("a");
  a.href = fileUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function generatePassword(length = 12) {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+[]{}|;:,.<>?";
  let password = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * chars.length);
    password += chars[randomIndex];
  }
  return password;
}

export const getSortQuery = (field, order = "ASC") => {
  return order === "DESC" ? `-${field}` : field;
};

export function getPaginationRange(currentPage, limit, total) {
  const start = (currentPage - 1) * limit + 1;
  const end = Math.min(currentPage * limit, total);
  return { start, end };
}

export const generateSlug = (text, id) => {
  return (
    text
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") + (id ? `-${id}` : "")
  );
};

export const supportedLanguages = [
  { code: "en", label: "English" },
  { code: "fr", label: "French" },
  { code: "es", label: "Spanish" },
  { code: "de", label: "German" },
];

export const userLang = localStorage.getItem("lang") || "en";
